import React from 'react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Store
import { AppProvider } from './store/AppContext';
// Routing
import routes from './routes/routes';
// Hooks
import useToast from './hooks/useToast';

// Sentry Config
import { isInSentryExceptionsAllowList } from './services/sentryAllowList';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],

  /**
   * @param {Sentry.Event} event
   * @param {object} hint
   * @param {import('axios').AxiosError | Error} hint.originalException
   * @return {Sentry.Event | null}
   */
  beforeSend(event, hint) {
    const error = hint.originalException;
    if (error instanceof AxiosError) {
      if (isInSentryExceptionsAllowList(error)) {
        return null;
      }
      return {
        ...event,
        message: error.response?.data?.code,
        extra: {
          ...event.extra,
          error: error.response?.data,
        },
      };
    }
    return event;
  },

  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  enabled: !import.meta.env.DEV,
  environment: import.meta.env.MODE,
});

// Router with Sentry
const sentryBrowserRouter = Sentry.wrapCreateBrowserRouter(
  createBrowserRouter,
);
const router = sentryBrowserRouter(routes);

function App() {
  // Hooks
  const toast = useToast();
  const { t } = useTranslation();

  /**
   * Create an error notification
   * @param {import('axios').AxiosError} error
   */
  const createErrorToastNotification = (error) => {
    const errorCode = error?.response?.data?.code;
    const message = t(errorCode ? `api.error.${errorCode}` : 'api.error.default');
    toast.notify({ message, type: 'alert' });
  };

  // React Query Client config
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
        refetchOnWindowFocus: false,
        throwOnError: true,
      },
      mutations: {
        throwOnError: true,
      },
    },
    mutationCache: new MutationCache({
      /**
       * @param {import('axios').AxiosError} error
       */
      onError: (error, _, __, mutation) => {
        if (!mutation?.options?.meta?.preventToastError) {
          createErrorToastNotification(error);
        }
      },
    }),
    queryCache: new QueryCache({
      /**
       * @param {import('axios').AxiosError} error
       */
      onError: (error, query) => {
        if (!query?.options?.meta?.preventToastError) {
          createErrorToastNotification(error);
        }
      },
    }),
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <RouterProvider router={router} />
      </AppProvider>
      <ToastContainer closeButton={false} />
      <ReactQueryDevtools initialIsOpen={import.meta.env.DEV} />
    </QueryClientProvider>
  );
}

export default App;
